import * as React from 'react'

interface DataTable {
  checked: boolean
  isDisabled?: boolean
  id: number | string
}

export const useTableMultiSelect = <P extends DataTable>(initialData: P[]) => {
  const [data, setData] = React.useState(initialData)
  const [checkedAll, setCheckedAll] = React.useState(false)
  const [indeterminate, setIndeterminate] = React.useState(false)
  const [canBeCheckedAll, setCanBeCheckedAll] = React.useState(false)

  const isIndeterminate = (checkedDataLength: number) =>
    0 < checkedDataLength && checkedDataLength < data.length

  const toggleChecked =
    (collection: P[], changeData: (data: P[]) => void) => () =>
      changeData(
        collection.map((item) => ({
          ...item,
          checked: item.isDisabled ? item.checked : !canBeCheckedAll,
        }))
      )

  const onCheckboxChange = (index: number | string) => () =>
    setData(
      data.map((item) =>
        item.id === index ? { ...item, checked: !item.checked } : item
      )
    )

  const checkedInfo = (collection: P[]) =>
    collection.reduce(
      (acc, item) => {
        if (item.checked) {
          return {
            isCheckedAll: acc.checkedDataLength + 1 === collection.length,
            checkedDataLength: acc.checkedDataLength + 1,
            canBeCheckedDataLength: acc.canBeCheckedDataLength + 1,
          }
        }
        return item.isDisabled
          ? acc
          : { ...acc, canBeCheckedDataLength: acc.canBeCheckedDataLength + 1 }
      },
      {
        isCheckedAll: false,
        checkedDataLength: 0,
        canBeCheckedDataLength: 0,
      }
    )

  React.useEffect(() => {
    const { isCheckedAll, checkedDataLength, canBeCheckedDataLength } =
      checkedInfo(data)

    setIndeterminate(isIndeterminate(checkedDataLength))
    setCheckedAll(isCheckedAll)
    setCanBeCheckedAll(checkedDataLength === canBeCheckedDataLength)
  }, [data])

  return {
    toggleChecked,
    data,
    indeterminate,
    checked: checkedAll,
    setData,
    onCheckboxChange,
  }
}
